<script lang="ts">
  // Vira — carta virada que define a manilha. Posicionada ao lado da mesa.
  import type { Card as CardType } from '../../stores/match';
  import { isRedSuit } from '../../lib/cards';

  type Props = { vira: CardType | null | undefined };
  let { vira }: Props = $props();

  // Cacheia a ultima vira valida pra evitar flicker: durante a mao a
  // vira nao muda, mas snaps intermediarios podem chegar com manilha=null
  // (push pos-PlayCard antes do save), o que reaplicaria {#key} e
  // disparava out:fade + reveal espurio. Felipe 2026-05-21: "vira ta
  // sumindo e aparecendo". So atualizo o cache quando vira tem valor.
  let cachedVira = $state<CardType | null>(null);
  let cachedKey = $state('');
  $effect(() => {
    if (vira?.value && vira?.suit) {
      const k = `${vira.value}${vira.suit}`;
      if (k !== cachedKey) {
        cachedVira = vira;
        cachedKey = k;
      }
    }
  });

  let hasVira = $derived(!!(cachedVira?.value && cachedVira?.suit));
  let isRed = $derived(hasVira && isRedSuit(cachedVira?.suit));
</script>

<div class="vira-wrap">
  <span class="label">VIRA</span>
  <!-- Pilha visual representando o "deck" — sempre presente atras da
       vira durante o jogo todo. 3 cartas-back empilhadas com offset
       leve. User pediu "atras do vira sempre uma carta virada" pra
       representar o baralho. -->
  <div class="deck-stack" aria-hidden="true">
    <div class="deck-card"></div>
    <div class="deck-card"></div>
    <div class="deck-card"></div>
  </div>
  {#if hasVira}
    <!-- {#key} usa cachedKey — so re-monta quando vira muda de verdade
         (fim de mao). Sem out:fade: Felipe 2026-05-21 "n pode sumir
         a carta vira" — carta velha some no MESMO momento que a nova
         entra (sem janela invisivel). -->
    {#key cachedKey}
      <div class="vira-card revealing" class:red={isRed}>
        <span class="value top">{cachedVira?.value}</span>
        <span class="suit center">{cachedVira?.suit}</span>
        <span class="value bottom">{cachedVira?.value}</span>
      </div>
    {/key}
  {:else}
    <div class="vira-card empty">—</div>
  {/if}
</div>

<style>
  .vira-wrap {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    /* perspective necessario pro rotateY do flip 3D na vira-reveal */
    perspective: 600px;
    position: relative;
    /* z-index acima do CinematicCollect overlay (z-index:10) — Felipe
     * 2026-05-21: anim de recolher passava POR CIMA da vira e player
     * achava que ela sumia. Agora a vira sempre fica visivel. */
    z-index: 12;
  }

  /* Deck-stack — pilha de 3 cartas back representando o baralho
   * remanescente. Position absolute, levemente atras+abaixo da vira,
   * com offset progressivo entre as 3 pra dar sensacao 3D. Sempre
   * visivel durante o jogo (Felipe 2026-05-19: "atras do vira sempre
   * uma carta virada"). */
  .deck-stack {
    position: absolute;
    /* mesma altura aproximada da vira-card */
    top: calc(50% - var(--card-w-md) * 0.55);
    left: calc(50% - var(--card-w-md) * 0.42);
    width: var(--card-w-md);
    height: calc(var(--card-w-md) * 1.5);
    pointer-events: none;
    z-index: 0;
    /* transform leve pra ficar atras da vira (que tem rotate(-6deg)) */
    transform: rotate(2deg);
  }
  .deck-card {
    position: absolute;
    inset: 0;
    background: linear-gradient(145deg, #5b2b8c, #2a0e52);
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 8px;
    box-shadow:
      0 2px 6px rgba(0, 0, 0, 0.5),
      inset 0 1px 0 rgba(255, 255, 255, 0.06);
  }
  .deck-card::before {
    content: '';
    position: absolute;
    inset: 5px;
    background:
      repeating-linear-gradient(45deg,
        rgba(255, 210, 74, 0.15) 0 5px,
        transparent 5px 10px);
    border-radius: 5px;
    opacity: 1;
  }
  /* Offset progressivo: 1a carta no fundo, 3a no topo (mais ofuscada) */
  .deck-card:nth-child(1) { transform: translate(-4px, -3px); }
  .deck-card:nth-child(2) { transform: translate(-2px, -1.5px); }
  .deck-card:nth-child(3) { transform: translate(0, 0); }

  /* vira-card ficaria em cima do deck (z-index maior) */
  .vira-card { position: relative; z-index: 1; }
  .label {
    font-size: 9px;
    letter-spacing: 2px;
    color: var(--text-dim);
    text-transform: uppercase;
  }
  .vira-card {
    width: var(--card-w-md);
    height: calc(var(--card-w-md) * 1.5);
    background: linear-gradient(180deg, #f7f4ee, #d8d2c4);
    color: #1a0e2e;
    border: 1px solid rgba(255, 210, 74, 0.5);
    border-radius: 8px;
    padding: 5px 7px;
    font-weight: 700;
    font-size: clamp(11px, 2vw, 14px);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    box-shadow:
      0 3px 8px rgba(0, 0, 0, 0.45),
      0 0 14px rgba(255, 210, 74, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
    transform: rotate(-6deg);
    transition: transform 0.2s;
  }
  .vira-card.red { color: #c41a4a; }
  .vira-card.empty {
    background: transparent;
    border-style: dashed;
    border-color: var(--border);
    color: var(--muted);
    align-items: center;
    justify-content: center;
    box-shadow: none;
  }
  .vira-card .value { line-height: 1; }
  .vira-card .value.bottom {
    align-self: flex-end;
    transform: rotate(180deg);
  }
  .vira-card .suit.center {
    align-self: center;
    font-size: 1.6em;
    line-height: 1;
  }
</style>
